import { Plus, Trash03, XClose } from "@untitledui/icons";
import { useEffect, useId, useMemo, useRef } from "react";
import { DialogTrigger as AriaDialogTrigger } from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Heading } from "@/components/Atoms/Heading/Heading";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { TextArea } from "@/components/base/textarea/textarea";
import { useAppForm } from "@/components/form";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import {
	type FormRule,
	type FormRuleAction,
	FormRuleActionType,
	type FormRuleActionTypeValue,
	type FormRuleCondition,
	FormRuleConditionLogic,
	type FormRuleConditionLogicValue,
	FormRuleConditionOperator,
	type FormRuleConditionOperatorValue,
	FORM_RULE_OPERATORS_WITHOUT_VALUE,
	getFormRuleOperatorsForDataType,
} from "../../types/form-rule-types";
import {
	getConditionValueInputKind,
	normalizeConditionValue,
} from "./form-rule-condition-value.util";
import {
	FormRuleOptionMultiValueInput,
	FormRuleOptionSingleValueInput,
	FormRuleTextValueInput,
} from "./form-rule-condition-value-input";

function newId(): string {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export interface FormRuleEditorFieldOption {
	id: string;
	label: string;
	dataType: string | undefined;
	options?: { value: string; label: string }[];
}

export interface FormRuleEditorModalProps {
	isOpen: boolean;
	mode: "create" | "edit";
	initialRule: FormRule | null;
	fields: FormRuleEditorFieldOption[];
	paths: AdminFormI18nPaths;
	onSubmit: (rule: FormRule) => void;
	onCancel: () => void;
	/**
	 * Stable id for one create or edit. Closing the dialog without changing
	 * this key keeps the draft. A new key, or clearing it, starts a fresh draft.
	 */
	editorSessionKey?: string | null;
}

const DEFAULT_LOGIC = FormRuleConditionLogic.all;

/** Gray card around one condition or action, matching the create-rule frame. */
const RULE_ITEM_CARD_CLASS =
	"flex items-center gap-4 rounded-xl border border-gray-300 bg-secondary p-4";

const RULE_FIELD_CONTROL_CLASS = "w-full";

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

function buildDraft(initialRule: FormRule | null): FormRule {
	if (initialRule) {
		return {
			...initialRule,
			description: initialRule.description ?? "",
		};
	}
	return {
		id: newId(),
		name: "",
		description: "",
		enabled: true,
		conditionLogic: DEFAULT_LOGIC,
		conditions: [],
		actions: [
			{
				id: newId(),
				type: FormRuleActionType.showField,
				targetFieldDefinitionId: "",
			},
		],
	};
}

function isOperatorAllowedForField(
	operator: FormRuleConditionOperatorValue,
	dataType: string | undefined,
): boolean {
	const allowed = getFormRuleOperatorsForDataType(dataType);
	return allowed.includes(operator);
}

function ensureOperatorForField(
	operator: FormRuleConditionOperatorValue,
	dataType: string | undefined,
): FormRuleConditionOperatorValue {
	if (isOperatorAllowedForField(operator, dataType)) return operator;
	const allowed = getFormRuleOperatorsForDataType(dataType);
	return allowed[0] ?? FormRuleConditionOperator.equals;
}

function conditionValueMissing(condition: FormRuleCondition): boolean {
	if (FORM_RULE_OPERATORS_WITHOUT_VALUE.has(condition.operator)) return false;
	const value = condition.value;
	if (value == null) return true;
	if (typeof value === "string") return value.trim() === "";
	if (Array.isArray(value)) return value.length === 0;
	return false;
}

function ruleSubmitError(
	value: FormRule,
	messages: {
		actionsRequired: string;
		conditionFieldRequired: string;
		actionTargetRequired: string;
		valueRequired: string;
	},
): string | undefined {
	if (value.actions.length === 0) return messages.actionsRequired;
	for (const condition of value.conditions) {
		if (!condition.fieldDefinitionId.trim()) {
			return messages.conditionFieldRequired;
		}
		if (conditionValueMissing(condition)) return messages.valueRequired;
	}
	for (const action of value.actions) {
		if (!action.type || !action.targetFieldDefinitionId.trim()) {
			return messages.actionTargetRequired;
		}
	}
	return undefined;
}

/**
 * Wraps `useAppForm` so the row sub-components can pin their `form` prop type
 * via `ReturnType<typeof useFormRuleEditorForm>` without re-deriving the deep
 * generic signature that `useAppForm` returns.
 */
function useFormRuleEditorForm(
	initialRule: FormRule | null,
	messages: {
		actionsRequired: string;
		conditionFieldRequired: string;
		actionTargetRequired: string;
		valueRequired: string;
	},
	onSubmit: (rule: FormRule) => void,
) {
	return useAppForm({
		defaultValues: buildDraft(initialRule),
		validators: {
			onSubmit: ({ value }) => ruleSubmitError(value, messages),
		},
		onSubmit: ({ value }) => {
			const now = new Date().toISOString();
			onSubmit({
				...value,
				name: value.name.trim(),
				description: value.description?.trim() || undefined,
				createdAt: value.createdAt ?? now,
				updatedAt: now,
			});
		},
	});
}

type FormRuleEditorForm = ReturnType<typeof useFormRuleEditorForm>;

/**
 * Centered modal editor for a single form rule. Built on TanStack Form via
 * `useAppForm` (mirrors the project's `create-sub-con.form.layout.tsx` pattern):
 * scalar fields use `form.AppField` + `field.InputField` with i18n
 * `validators.onChange`, the `conditions` and `actions` field arrays use
 * `<form.Field mode="array">` with `pushValue` / `removeValue`, cross-field
 * operator/value clearing is done imperatively via `form.setFieldValue`, and
 * the footer Save button + inline actions-required hint subscribe through
 * `form.Subscribe`. Conditions are optional; the All/Any logic selector is
 * always shown and defaults to `"all"`. Actions require at least one row.
 */
export function FormRuleEditorModal({
	isOpen,
	mode,
	initialRule,
	fields,
	paths,
	onSubmit,
	onCancel,
	editorSessionKey,
}: FormRuleEditorModalProps) {
	const { t } = useAppTranslation();
	const titleId = useId();

	const fieldsById = useMemo(() => {
		const map = new Map<string, FormRuleEditorFieldOption>();
		for (const f of fields) {
			map.set(f.id, f);
		}
		return map;
	}, [fields]);

	const fieldSelectOptions = useMemo(
		() => [
			{ value: "", label: t(paths.formRules.modal.selectField) },
			...fields.map((f) => ({ value: f.id, label: f.label })),
		],
		[fields, paths.formRules.modal.selectField, t],
	);

	const actionTypeOptions = useMemo(
		() => [
			{ value: "", label: t(paths.formRules.modal.selectAction) },
			...Object.values(FormRuleActionType).map((value) => ({
				value,
				label: t(paths.formRules.actions[value]),
			})),
		],
		[paths.formRules.actions, paths.formRules.modal.selectAction, t],
	);

	const operatorOptionsFor = (dataType: string | undefined) => {
		const operators = getFormRuleOperatorsForDataType(dataType);
		return [
			{ value: "", label: t(paths.formRules.modal.selectOperator) },
			...operators.map((op) => ({
				value: op,
				label: t(paths.formRules.operators[op]),
			})),
		];
	};

	const form = useFormRuleEditorForm(
		initialRule,
		{
			actionsRequired: t(paths.formRules.validation.actionsRequired),
			conditionFieldRequired: t(
				paths.formRules.validation.conditionFieldRequired,
			),
			actionTargetRequired: t(paths.formRules.validation.actionTargetRequired),
			valueRequired: t(paths.formRules.validation.valueRequired),
		},
		onSubmit,
	);

	const appliedSessionKey = useRef<string | null>(null);
	useEffect(() => {
		if (editorSessionKey === undefined) {
			if (isOpen) form.reset(buildDraft(initialRule));
			return;
		}
		if (!editorSessionKey) {
			appliedSessionKey.current = null;
			return;
		}
		if (!isOpen || appliedSessionKey.current === editorSessionKey) return;
		appliedSessionKey.current = editorSessionKey;
		form.reset(buildDraft(initialRule));
	}, [editorSessionKey, isOpen, initialRule, form]);

	const modalTitle =
		mode === "create"
			? t(paths.formRules.modal.createTitle)
			: t(paths.formRules.modal.editTitle);

	const addCondition = () => {
		const c: FormRuleCondition = {
			id: newId(),
			fieldDefinitionId: "",
			operator: FormRuleConditionOperator.equals,
		};
		form.pushFieldValue("conditions", c);
	};

	const addAction = () => {
		const a: FormRuleAction = {
			id: newId(),
			type: FormRuleActionType.showField,
			targetFieldDefinitionId: "",
		};
		form.pushFieldValue("actions", a);
	};

	return (
		<AriaDialogTrigger
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<ModalOverlay isDismissable={false}>
				<Modal className="max-w-[500px]">
					<Dialog aria-labelledby={titleId}>
						<div className="flex max-h-[min(860px,calc(100dvh-64px))] w-full flex-col overflow-hidden rounded-2xl bg-primary shadow-xl">
							<div className="flex shrink-0 items-start gap-4 border-b border-primary px-6 py-4">
								<div className="flex min-w-0 flex-1 flex-col items-start justify-center">
									<Heading id={titleId} level="h3" className="outline-hidden">
										{modalTitle}
									</Heading>
								</div>
								<IconButton
									aria-label={t(paths.formRules.modal.close)}
									hierarchy="link"
									size="sm"
									shape="round"
									icon={(iconProps) => (
										<XClose {...iconProps} strokeWidth={2.5} />
									)}
									onPress={onCancel}
									className="size-9! shrink-0 p-2! text-primary *:data-icon:text-primary"
								/>
							</div>

							<div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
								<form
									className="flex flex-col gap-6"
									onSubmit={(e) => {
										e.preventDefault();
										e.stopPropagation();
										form.handleSubmit();
									}}
								>
									<form.AppField
										name="name"
										validators={{
											onChange: ({ value }: { value: string }) =>
												value.trim().length === 0
													? t(paths.formRules.validation.nameRequired)
													: undefined,
										}}
										children={(field) => (
											<field.InputField
												label={t(paths.formRules.modal.name)}
												placeholder={t(paths.formRules.modal.namePlaceholder)}
												isRequired
												maxLength={NAME_MAX_LENGTH}
											/>
										)}
									/>

									<form.AppField
										name="description"
										children={(field) => (
											<TextArea
												label={t(paths.formRules.modal.description)}
												placeholder={t(
													paths.formRules.modal.descriptionPlaceholder,
												)}
												value={(field.state.value as string | undefined) ?? ""}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												maxLength={DESCRIPTION_MAX_LENGTH}
												rows={6}
												textAreaClassName="min-h-[140px]"
											/>
										)}
									/>

									<form.Field name="conditionLogic">
										{(field) => (
											<NativeSelect
												label={t(paths.formRules.modal.conditionsSection)}
												value={field.state.value}
												onChange={(e) => {
													field.handleChange(
														e.target.value as FormRuleConditionLogicValue,
													);
												}}
												options={[
													{
														value: FormRuleConditionLogic.all,
														label: t(
															paths.formRules.conditionLogic.allMatchHint,
														),
													},
													{
														value: FormRuleConditionLogic.any,
														label: t(
															paths.formRules.conditionLogic.anyMatchHint,
														),
													},
												]}
												selectClassName="py-2"
											/>
										)}
									</form.Field>

									<form.Field name="conditions" mode="array">
										{(arrayField) => (
											<>
												{arrayField.state.value.length > 0 ? (
													<ul className="flex flex-col gap-6">
														{arrayField.state.value.map(
															(c: FormRuleCondition, idx: number) => (
																<ConditionRow
																	key={c.id}
																	form={form}
																	idx={idx}
																	fieldsById={fieldsById}
																	fieldSelectOptions={fieldSelectOptions}
																	operatorOptionsFor={operatorOptionsFor}
																	removeLabel={t(
																		paths.formRules.modal.removeCondition,
																	)}
																	selectFieldLabel={t(
																		paths.formRules.modal.selectField,
																	)}
																	selectOperatorLabel={t(
																		paths.formRules.modal.selectOperator,
																	)}
																	valueLabel={t(
																		paths.formRules.modal.conditionValueLabel,
																	)}
																	valuePlaceholder={t(
																		paths.formRules.modal.valuePlaceholder,
																	)}
																	onRemove={() => arrayField.removeValue(idx)}
																/>
															),
														)}
													</ul>
												) : null}
												<Button
													type="button"
													size="sm"
													color="secondary"
													iconLeading={Plus}
													onClick={addCondition}
												>
													{t(paths.formRules.modal.addCondition)}
												</Button>
											</>
										)}
									</form.Field>

									<form.Subscribe
										selector={(state) => state.errorMap.onSubmit}
									>
										{(error) =>
											typeof error === "string" && error.length > 0 ? (
												<p className="text-xs text-error-primary">{error}</p>
											) : null
										}
									</form.Subscribe>

									<form.Field name="actions" mode="array">
										{(arrayField) => (
											<>
												{arrayField.state.value.length > 0 ? (
													<ul className="flex flex-col gap-6">
														{arrayField.state.value.map(
															(a: FormRuleAction, idx: number) => (
																<ActionRow
																	key={a.id}
																	form={form}
																	idx={idx}
																	actionTypeOptions={actionTypeOptions}
																	fieldSelectOptions={fieldSelectOptions}
																	thenLabel={t(
																		paths.formRules.modal.actionsSection,
																	)}
																	targetFieldLabel={t(
																		paths.formRules.modal.selectField,
																	)}
																	removeLabel={t(
																		paths.formRules.modal.removeAction,
																	)}
																	onRemove={() => arrayField.removeValue(idx)}
																/>
															),
														)}
													</ul>
												) : null}
												<Button
													type="button"
													size="sm"
													color="secondary"
													iconLeading={Plus}
													onClick={addAction}
												>
													{t(paths.formRules.modal.addAction)}
												</Button>
											</>
										)}
									</form.Field>
								</form>
							</div>

							<div className="grid shrink-0 grid-cols-2 gap-4 border-t border-secondary px-6 py-6">
								<Button color="secondary" size="lg" onClick={onCancel}>
									{t(paths.formRules.modal.cancel)}
								</Button>
								<form.Subscribe
									selector={(state) => [state.isSubmitting] as const}
								>
									{([isSubmitting]) => (
										<Button
											color="primary"
											size="lg"
											isLoading={isSubmitting}
											showTextWhileLoading
											onClick={() => form.handleSubmit()}
										>
											{t(paths.formRules.modal.save)}
										</Button>
									)}
								</form.Subscribe>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
}

function RemoveRuleItemButton({
	removeLabel,
	onRemove,
}: {
	removeLabel: string;
	onRemove: () => void;
}) {
	return (
		<IconButton
			hierarchy="secondary"
			size="md"
			shape="square"
			icon={Trash03}
			aria-label={removeLabel}
			onPress={onRemove}
		/>
	);
}

interface ConditionRowProps {
	form: FormRuleEditorForm;
	idx: number;
	fieldsById: Map<string, FormRuleEditorFieldOption>;
	fieldSelectOptions: { value: string; label: string }[];
	operatorOptionsFor: (
		dataType: string | undefined,
	) => { value: string; label: string }[];
	valueLabel: string;
	removeLabel: string;
	selectFieldLabel: string;
	selectOperatorLabel: string;
	valuePlaceholder: string;
	onRemove: () => void;
}

function ConditionRow({
	form,
	idx,
	fieldsById,
	fieldSelectOptions,
	operatorOptionsFor,
	valueLabel,
	removeLabel,
	selectFieldLabel,
	selectOperatorLabel,
	valuePlaceholder,
	onRemove,
}: ConditionRowProps) {
	const fieldDefPath = `conditions[${idx}].fieldDefinitionId` as const;
	const operatorPath = `conditions[${idx}].operator` as const;
	const valuePath = `conditions[${idx}].value` as const;

	return (
		<li className={RULE_ITEM_CARD_CLASS}>
			<div className="flex min-w-0 flex-1 flex-col gap-3">
					<form.Field name={fieldDefPath as never}>
						{(field) => (
							<NativeSelect
								className={RULE_FIELD_CONTROL_CLASS}
								aria-label={selectFieldLabel}
								value={(field.state.value as string) ?? ""}
								onChange={(e) => {
									const fieldDefinitionId = e.target.value;
									field.handleChange(fieldDefinitionId as never);
									const fieldOption = fieldsById.get(fieldDefinitionId);
									const dataType = fieldOption?.dataType;
									const currentOperator = form.getFieldValue(
										operatorPath as never,
									) as FormRuleConditionOperatorValue;
									const nextOperator = ensureOperatorForField(
										currentOperator,
										dataType,
									);
									if (nextOperator !== currentOperator) {
										form.setFieldValue(
											operatorPath as never,
											nextOperator as never,
										);
									}
									const inputKind = getConditionValueInputKind(
										dataType,
										nextOperator,
										fieldOption?.options,
									);
									form.setFieldValue(
										valuePath as never,
										normalizeConditionValue(undefined, inputKind) as never,
									);
								}}
								onBlur={field.handleBlur}
								options={fieldSelectOptions}
								selectClassName="py-2"
							/>
						)}
					</form.Field>

					<form.Subscribe
						selector={(s) => {
							const conditions = (s.values as FormRule).conditions;
							return conditions[idx]?.fieldDefinitionId ?? "";
						}}
					>
						{(fieldDefinitionId) => {
							const dataType = fieldsById.get(fieldDefinitionId)?.dataType;
							return (
								<form.Field name={operatorPath as never}>
									{(field) => (
										<NativeSelect
											className={RULE_FIELD_CONTROL_CLASS}
											aria-label={selectOperatorLabel}
											value={(field.state.value as string) ?? ""}
											onChange={(e) => {
												const op = e.target
													.value as FormRuleConditionOperatorValue;
												field.handleChange(op as never);
												const fieldOption = fieldsById.get(fieldDefinitionId);
												const inputKind = getConditionValueInputKind(
													fieldOption?.dataType,
													op,
													fieldOption?.options,
												);
												const currentValue = form.getFieldValue(
													valuePath as never,
												) as string | number | boolean | string[] | undefined;
												form.setFieldValue(
													valuePath as never,
													normalizeConditionValue(
														currentValue,
														inputKind,
													) as never,
												);
											}}
											onBlur={field.handleBlur}
											options={operatorOptionsFor(dataType)}
											selectClassName="py-2"
										/>
									)}
								</form.Field>
							);
						}}
					</form.Subscribe>

					<form.Subscribe
						selector={(s) => {
							const condition = (s.values as FormRule).conditions[idx];
							return [
								condition?.fieldDefinitionId ?? "",
								condition?.operator,
							] as const;
						}}
					>
						{([fieldDefinitionId, operator]) => {
							if (operator === undefined) {
								return null;
							}

							const fieldOption = fieldsById.get(fieldDefinitionId);
							const inputKind = getConditionValueInputKind(
								fieldOption?.dataType,
								operator,
								fieldOption?.options,
							);

							if (inputKind === "none") {
								return null;
							}

							return (
								<form.Field name={valuePath as never}>
									{(field) => {
										const rawValue = field.state.value as
											| string
											| number
											| boolean
											| string[]
											| undefined;

										if (inputKind === "option_single") {
											return (
												<FormRuleOptionSingleValueInput
													label={valueLabel}
													value={
														(typeof rawValue === "string"
															? rawValue
															: Array.isArray(rawValue)
																? (rawValue[0] ?? "")
																: "") ?? ""
													}
													options={fieldOption?.options ?? []}
													onChange={(next) => field.handleChange(next as never)}
													onBlur={field.handleBlur}
												/>
											);
										}

										if (inputKind === "option_multi") {
											const multiValue = Array.isArray(rawValue)
												? rawValue
												: typeof rawValue === "string" && rawValue
													? [rawValue]
													: [];
											return (
												<FormRuleOptionMultiValueInput
													label={valueLabel}
													value={multiValue}
													options={fieldOption?.options ?? []}
													placeholder={valuePlaceholder}
													onChange={(next) => field.handleChange(next as never)}
													onBlur={field.handleBlur}
												/>
											);
										}

										const textValue =
											typeof rawValue === "string"
												? rawValue
												: Array.isArray(rawValue)
													? rawValue.join(", ")
													: rawValue === undefined
														? ""
														: String(rawValue);

										return (
											<FormRuleTextValueInput
												label={valueLabel}
												value={textValue}
												placeholder={valuePlaceholder}
												onChange={(next) => field.handleChange(next as never)}
												onBlur={field.handleBlur}
											/>
										);
									}}
								</form.Field>
							);
						}}
					</form.Subscribe>
			</div>

			<RemoveRuleItemButton removeLabel={removeLabel} onRemove={onRemove} />
		</li>
	);
}

interface ActionRowProps {
	form: FormRuleEditorForm;
	idx: number;
	actionTypeOptions: { value: string; label: string }[];
	fieldSelectOptions: { value: string; label: string }[];
	thenLabel: string;
	targetFieldLabel: string;
	removeLabel: string;
	onRemove: () => void;
}

function ActionRow({
	form,
	idx,
	actionTypeOptions,
	fieldSelectOptions,
	thenLabel,
	targetFieldLabel,
	removeLabel,
	onRemove,
}: ActionRowProps) {
	const typePath = `actions[${idx}].type` as const;
	const targetPath = `actions[${idx}].targetFieldDefinitionId` as const;

	return (
		<li className={RULE_ITEM_CARD_CLASS}>
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<form.Field name={typePath as never}>
					{(field) => (
						<NativeSelect
							className={RULE_FIELD_CONTROL_CLASS}
							label={thenLabel}
							isRequired
							value={(field.state.value as string) ?? ""}
							onChange={(e) =>
								field.handleChange(
									e.target.value as FormRuleActionTypeValue as never,
								)
							}
							onBlur={field.handleBlur}
							options={actionTypeOptions}
							selectClassName="py-2"
						/>
					)}
				</form.Field>

				<form.Field name={targetPath as never}>
					{(field) => (
						<NativeSelect
							className={RULE_FIELD_CONTROL_CLASS}
							aria-label={targetFieldLabel}
							value={(field.state.value as string) ?? ""}
							onChange={(e) => field.handleChange(e.target.value as never)}
							onBlur={field.handleBlur}
							options={fieldSelectOptions}
							selectClassName="py-2"
						/>
					)}
				</form.Field>
			</div>

			<RemoveRuleItemButton removeLabel={removeLabel} onRemove={onRemove} />
		</li>
	);
}
